import React, { useState, useEffect, useCallback } from 'react';
import { Receipt, Upload, Clock, Check, X, AlertTriangle, ExternalLink, FileText, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADO_CONFIG = {
  subida: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Subida' },
  procesada: { bg: 'bg-green-100', text: 'text-green-800', label: 'Procesada' },
  rechazada: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rechazada' },
};

const SubidaFacturas = () => {
  const { token, user } = useAuth();
  const [facturas, setFacturas] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [selectedEmpleado, setSelectedEmpleado] = useState('');
  const [stats, setStats] = useState(null);

  // Admin views
  const [vista, setVista] = useState('subir'); // subir | historial
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroEmpleado, setFiltroEmpleado] = useState('');

  const headers = { 'Authorization': `Bearer ${token}` };
  const isAdmin = user?.rol === 'direccion' || user?.rol === 'rrhh';

  const fetchData = useCallback(async () => {
    try {
      if (isAdmin) {
        const [empRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/api/empleados`, { headers }),
          fetch(`${API_URL}/api/facturas-empleados/estadisticas`, { headers }),
        ]);
        if (empRes.ok) setEmpleados(await empRes.json());
        if (statsRes.ok) setStats(await statsRes.json());
      } else {
        const statsRes = await fetch(`${API_URL}/api/facturas-empleados/estadisticas`, { headers });
        if (statsRes.ok) setStats(await statsRes.json());
      }

      // Fetch facturas based on view: el backend resuelve el empleado desde el token
      const url = isAdmin
        ? `${API_URL}/api/facturas-empleados/historial`
        : `${API_URL}/api/facturas-empleados/mis-facturas`;

      const res = await fetch(url, { headers });
      if (res.ok) setFacturas(await res.json());
    } catch (e) { console.error(e); }
  }, [token, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showMsg = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje(null), 5000);
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const empId = selectedEmpleado || user?.empleado_id || '';

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('empleado_id', empId);

        const res = await fetch(`${API_URL}/api/facturas-empleados/subir`, {
          method: 'POST', headers, body: formData
        });

        if (res.ok) {
          const data = await res.json();
          successCount++;
          if (data.convertida_a_pdf) {
            showMsg('ok', `${file.name} convertida a PDF y subida correctamente`);
          }
        } else {
          errorCount++;
          const err = await res.json();
          showMsg('error', `Error subiendo ${file.name}: ${err.detail}`);
        }
      } catch (e) {
        errorCount++;
      }
    }

    if (successCount > 0) {
      showMsg('ok', `${successCount} factura(s) subida(s) correctamente${errorCount > 0 ? `. ${errorCount} error(es).` : ''}`);
    }

    setUploading(false);
    fetchData();
  };

  const handleRechazar = async (facturaId, notas) => {
    try {
      const formData = new FormData();
      formData.append('notas_rechazo', notas);
      formData.append('rechazada_por', user?.nombre || 'admin');

      const res = await fetch(`${API_URL}/api/facturas-empleados/rechazar/${facturaId}`, {
        method: 'PATCH', headers, body: formData
      });
      if (res.ok) {
        showMsg('ok', 'Factura rechazada y eliminada de Drive');
        fetchData();
      }
    } catch (e) { showMsg('error', 'Error al rechazar'); }
  };

  const facturasFiltradas = facturas.filter(f => {
    if (filtroEstado && f.estado !== filtroEstado) return false;
    if (filtroEmpleado && f.empleado_id !== filtroEmpleado) return false;
    return true;
  });

  const empleadosActivos = empleados.filter(e => e.activo !== false);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="subida-facturas-page">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-xl">
              <Receipt className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Subida de Facturas</h1>
              <p className="text-sm text-gray-500">Sube tus facturas para que se procesen automáticamente</p>
            </div>
          </div>
        </div>

        {/* Mensaje */}
        {mensaje && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
            mensaje.tipo === 'ok' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {mensaje.tipo === 'ok' ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {mensaje.texto}
          </div>
        )}

        {/* Stats (admin) */}
        {isAdmin && stats && (
          <div className="grid grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Total', value: stats.total, color: 'text-gray-900' },
              { label: 'Subidas', value: stats.subidas, color: 'text-blue-600' },
              { label: 'Procesadas', value: stats.procesadas, color: 'text-green-600' },
              { label: 'Rechazadas', value: stats.rechazadas, color: 'text-red-600' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border p-4 text-center shadow-sm">
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs (admin) */}
        {isAdmin && (
          <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm border mb-6">
            {[
              { id: 'subir', label: 'Subir Factura', icon: Upload },
              { id: 'historial', label: 'Historial', icon: Clock },
            ].map(t => (
              <button key={t.id} onClick={() => setVista(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  vista === t.id ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'
                }`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Selector de empleado (admin) */}
        {isAdmin && vista === 'subir' && (
          <div className="bg-white rounded-xl border p-4 shadow-sm mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Subir factura en nombre de (opcional):</label>
            <select value={selectedEmpleado} onChange={e => setSelectedEmpleado(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm w-full max-w-md" data-testid="select-empleado-factura">
              <option value="">Yo mismo</option>
              {empleadosActivos.map(e => (
                <option key={e.id} value={e.id}>{e.nombre} {e.apellidos}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Si no eliges a nadie, la factura se registra a tu nombre.</p>
          </div>
        )}

        {/* Upload area */}
        {(vista === 'subir' || !isAdmin) && (
          <div className="bg-white rounded-xl border shadow-sm mb-6">
            <div
              className={`border-2 border-dashed rounded-xl m-4 p-12 text-center transition-colors ${
                uploading ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 hover:border-emerald-400 hover:bg-emerald-50/50'
              }`}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={e => {
                e.preventDefault();
                e.stopPropagation();
                handleUpload(e.dataTransfer.files);
              }}
              data-testid="upload-drop-zone"
            >
              {uploading ? (
                <div className="animate-pulse">
                  <Upload className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <p className="text-emerald-600 font-medium">Subiendo y procesando...</p>
                  <p className="text-xs text-gray-400 mt-1">Las imágenes se convierten automáticamente a PDF</p>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-gray-600 font-medium mb-1">Arrastra tus facturas aquí</p>
                  <p className="text-xs text-gray-400 mb-4">o haz click para seleccionar archivos</p>
                  <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 cursor-pointer transition"
                    data-testid="btn-seleccionar-facturas">
                    <Upload className="w-4 h-4" /> Seleccionar facturas
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                      multiple
                      className="hidden"
                      onChange={e => handleUpload(e.target.files)}
                    />
                  </label>
                  <p className="text-xs text-gray-400 mt-3">PDF, JPG o PNG (máx. 15MB). Las imágenes se convierten a PDF.</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Historial (admin) */}
        {isAdmin && vista === 'historial' && (
          <div className="space-y-4">
            <div className="flex gap-3">
              <select value={filtroEmpleado} onChange={e => setFiltroEmpleado(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm">
                <option value="">Todos los empleados</option>
                {empleadosActivos.map(e => (
                  <option key={e.id} value={e.id}>{e.nombre} {e.apellidos}</option>
                ))}
              </select>
              <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm">
                <option value="">Todos los estados</option>
                <option value="subida">Subidas</option>
                <option value="procesada">Procesadas</option>
                <option value="rechazada">Rechazadas</option>
              </select>
            </div>

            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Archivo</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Empleado</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Fecha Subida</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Estado</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {facturasFiltradas.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-12 text-gray-400">No hay facturas</td></tr>
                  ) : (
                    facturasFiltradas.map(f => {
                      const config = ESTADO_CONFIG[f.estado] || ESTADO_CONFIG.subida;
                      return (
                        <tr key={f.id} className="border-b hover:bg-gray-50" data-testid={`factura-row-${f.id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-gray-400" />
                              <div>
                                <div className="font-medium text-gray-900 text-xs">{f.archivo_nombre_original}</div>
                                {f.notas_rechazo && (
                                  <div className="text-xs text-red-600 mt-0.5 flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> {f.notas_rechazo}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-700 text-sm">{f.empleado_nombre}</td>
                          <td className="px-4 py-3 text-gray-600 text-xs">
                            {new Date(f.fecha_subida).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                              {config.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              {f.drive_link && (
                                <a href={f.drive_link} target="_blank" rel="noopener noreferrer"
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Ver en Drive">
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                              {f.estado === 'subida' && (
                                <button
                                  onClick={() => {
                                    const notas = prompt('Motivo del rechazo:');
                                    if (notas) handleRechazar(f.id, notas);
                                  }}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Rechazar">
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Employee's own history */}
        {!isAdmin && facturas.length > 0 && (
          <div className="bg-white rounded-xl border shadow-sm p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Mis facturas subidas</h3>
            <div className="space-y-2">
              {facturas.map(f => {
                const config = ESTADO_CONFIG[f.estado] || ESTADO_CONFIG.subida;
                return (
                  <div key={f.id} className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-800">{f.archivo_nombre_original}</span>
                        <span className="text-xs text-gray-400 ml-2">
                          {new Date(f.fecha_subida).toLocaleDateString('es-ES')}
                        </span>
                        {f.notas_rechazo && (
                          <div className="text-xs text-red-600 mt-0.5">Motivo: {f.notas_rechazo}</div>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                      {config.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-700">
          <strong>Nota:</strong> Las imágenes (JPG, PNG) se convierten automáticamente a PDF antes de guardarse en Drive.
          {isAdmin && ' Puedes rechazar facturas desde aquí o desde la vista de Drive en Finanzas.'}
        </div>
      </div>
    </div>
  );
};

export default SubidaFacturas;
